import { useFocusEffect } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import { useCallback, useEffect, useState } from 'react';

import { setConfettiEnabled } from '@/components/ConfettiOverlay';
import { setPreloaderEnabled } from '@/components/Preloader';
import type { PropertyRow } from '@/db/types';
import { useDataRevision } from '@/lib/data-revision';
import {
  type ActiveTask,
  computeProfile,
  countProfileAttributes,
  getActiveProperty,
  getActiveTasks,
  getDoneCountThisWeek,
  getSeasonProgress,
  getStreakDays,
  getSubtypeCoverageSignal,
  listProperties,
  partitionTasks,
  profileAttributeChecks,
  shouldSuggestReplan,
  type PartitionedTasks,
  type SeasonProgress,
} from '@/lib/tasks';

export interface HomeData {
  loading: boolean;
  property: PropertyRow | null;
  /** Raw active tasks — used by the 14-day calendar peek strip. */
  tasks: ActiveTask[];
  partition: PartitionedTasks;
  doneThisWeek: number;
  ringPct: number;
  /** Completion-based season progress; 0% on a fresh setup. */
  season: SeasonProgress;
  /** Consecutive-day completion streak (0 if today's the first day). */
  streak: number;
  /** Count of systems that have a subtype-eligible kind but haven't
   *  had a subtype picked yet. Powers the "Tune N systems" chip on
   *  the Home tab. 0 means the chip is hidden. */
  systemsToTune: number;
  /** Count of profile attribute fields still blank. Powers the
   *  "Finish profile" chip on Home. */
  attributesBlank: number;
  /** Label of the first blank attribute, if any — used in the chip
   *  body so the action reads concrete ("Add water source"). */
  firstBlankLabel: string | null;
  /** Field key of the first blank attribute, e.g. 'heating_fuel'. The
   *  Home tab's "Add X" chip uses this to deep-link to the right card
   *  in the Tune walkthrough. */
  firstBlankField: string | null;
  /** Total number of homes the user has. Used by the Home tab to show
   *  an "at [Home Name]" tagline only when there are 2+ homes — the
   *  disambiguator is unnecessary clutter for single-home users. */
  homeCount: number;
  /** True when last_replan_at is older than ~11 months. Drives the
   *  annual "Time to replan" chip on Home. */
  suggestReplan: boolean;
  refresh: () => Promise<void>;
}

/**
 * Single hook that loads everything the Home tab needs in parallel and
 * re-fetches when the screen regains focus (so completing a task in the
 * Schedule tab is reflected when the user comes back).
 */
export function useHomeData(): HomeData {
  const db = useSQLiteContext();
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState<PropertyRow | null>(null);
  const [tasks, setTasks] = useState<ActiveTask[]>([]);
  const [doneThisWeek, setDoneThisWeek] = useState(0);
  // Start at 0, not 30: the new profile formula gives no free baseline,
  // so the ring should reflect "nothing earned yet" until real data
  // lands on the first focus tick (which happens within a render or two).
  const [ringPct, setRingPct] = useState(0);
  const [systemsToTune, setSystemsToTune] = useState(0);
  const [attributesBlank, setAttributesBlank] = useState(0);
  const [firstBlankLabel, setFirstBlankLabel] = useState<string | null>(null);
  // Field key (e.g. 'heating_fuel') of the first blank attribute. Used
  // by the Home tab's "Add X" chip to deep-link directly to that card
  // in the Tune walkthrough instead of starting at card 1.
  const [firstBlankField, setFirstBlankField] = useState<string | null>(null);
  const [season, setSeason] = useState<SeasonProgress>({
    season: 'Spring', completed: 0, total: 0, pct: 0,
  });
  const [streak, setStreak] = useState(0);
  const [homeCount, setHomeCount] = useState(1);
  const [suggestReplan, setSuggestReplan] = useState(false);

  const fetch = useCallback(async () => {
    const prop = await getActiveProperty(db);
    // Run home-count alongside the active-property fetch so the Home
    // tab can decide whether to show the "at [Home Name]" tagline (only
    // multi-home users need it; single-home users see no clutter).
    const allHomes = await listProperties(db);
    setHomeCount(allHomes.length);
    if (!prop) {
      setProperty(null);
      setTasks([]);
      setDoneThisWeek(0);
      setRingPct(0);
      setLoading(false);
      return;
    }
    // Fan out the profile-signal queries in parallel. Each is a single
    // SQLite SELECT — total round-trip is one tick.
    const [
      activeTasks,
      done,
      subtypeCov,
      seasonProg,
      streakDays,
    ] = await Promise.all([
      // Bumped from 60 → 200 so rescheduled tasks pushed to the back of the
      // queue still surface on the home screen instead of getting clipped.
      getActiveTasks(db, prop.id, 200),
      getDoneCountThisWeek(db, prop.id),
      getSubtypeCoverageSignal(db, prop.id),
      getSeasonProgress(db, prop.id),
      getStreakDays(db, prop.id),
    ]);
    const attrs = countProfileAttributes(prop);
    // Build the per-field blanks list so the Home "Finish profile"
    // chip can name the first missing field by label. Matches the
    // ordering used by the My Home health card so users see a
    // consistent priority order across surfaces.
    const checks = profileAttributeChecks(prop);
    const blanks = checks.filter((c) => !c.filled);
    setAttributesBlank(blanks.length);
    setFirstBlankLabel(blanks[0]?.label ?? null);
    setFirstBlankField(blanks[0] ? String(blanks[0].field) : null);
    setProperty(prop);
    setTasks(activeTasks);
    setDoneThisWeek(done);
    setRingPct(
      computeProfile(prop, {
        attributesFilled: attrs.filled,
        attributesTotal: attrs.total,
        subtypeEligibleSystems: subtypeCov.subtypeEligibleSystems,
        subtypesSelected: subtypeCov.subtypesSelected,
        hasCompletedAnyTask: false, // no longer used by the formula
      })
    );
    // Derived from the same subtype coverage signal: how many systems
    // still need a chip pick. Drives the Home-tab "Tune N systems"
    // chip and the My Home health card subtitle.
    setSystemsToTune(
      Math.max(0, subtypeCov.subtypeEligibleSystems - subtypeCov.subtypesSelected)
    );
    setSeason(seasonProg);
    setStreak(streakDays);
    setSuggestReplan(shouldSuggestReplan(prop));
    // Confetti and preloader are now always-on globally. The toggles were
    // removed from Settings; we no longer read the property prefs for them.
    // (DB columns remain to avoid migration churn for older rows.)
    setConfettiEnabled(true);
    setPreloaderEnabled(true);
    setLoading(false);
  }, [db]);

  useFocusEffect(
    useCallback(() => {
      fetch();
    }, [fetch])
  );

  // Refetch when any DB write fires the data revision — covers
  // home-switch (HomeSwitcher → setActiveProperty → bumpDataRevision)
  // and any inline edits that mutate property state. Without this the
  // Home ring + season chips + streak would keep showing the previous
  // home's numbers after a switch.
  const revision = useDataRevision();
  useEffect(() => {
    void fetch();
  }, [revision, fetch]);

  return {
    loading,
    property,
    tasks,
    partition: partitionTasks(tasks),
    doneThisWeek,
    ringPct,
    season,
    streak,
    systemsToTune,
    attributesBlank,
    firstBlankLabel,
    firstBlankField,
    homeCount,
    suggestReplan,
    refresh: fetch,
  };
}
